/*
 * OOO side cart controller (loaded globally, deferred).
 *  - window.OOOCart = { open, close, refresh } so any script can drive it
 *    (e.g. a bundle "add all" flow) and the header cart icon opens it.
 *  - ONE global submit listener on form[action*="/cart/add"] intercepts
 *    every add -> AJAX add -> swaps the drawer panel -> opens it.
 *  - CRITICAL: the store may have NO products. If the form has no variant
 *    id (blank "id" field), the add no-ops gracefully (no failed fetch,
 *    no broken UI) — it just opens the drawer to its current (empty) state.
 *  - Qty +/- and remove use /cart/change.js (delegated).
 *  - applySection() swaps .ooo-cart__panel innerHTML via the Section
 *    Rendering API (sections=ooo-cart-drawer). Open state on <html>.
 *  - updateBadge() bounces the cart icon when the count rises.
 */
(function () {
  'use strict';

  var SECTION_ID = 'ooo-cart-drawer';
  var root = document.documentElement;
  var lastFocus = null;

  function drawer() { return document.querySelector('[data-ooo-cart]'); }
  function panel() { return document.querySelector('.ooo-cart__panel'); }

  function open() {
    if (!drawer()) return;
    lastFocus = document.activeElement;
    root.classList.add('ooo-cart-open');
    drawer().setAttribute('aria-hidden', 'false');
    var closeBtn = document.querySelector('.ooo-cart__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }
  function close() {
    root.classList.remove('ooo-cart-open');
    if (drawer()) drawer().setAttribute('aria-hidden', 'true');
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }
  function setBusy(b) { if (drawer()) drawer().setAttribute('aria-busy', b ? 'true' : 'false'); }

  function applySection(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('.ooo-cart__panel');
    var live = panel();
    if (fresh && live) live.innerHTML = fresh.innerHTML;
    var freshRoot = doc.querySelector('[data-ooo-cart]');
    if (freshRoot && drawer()) {
      var count = freshRoot.dataset.itemCount;
      drawer().dataset.itemCount = count;
      updateBadge(count);
    }
  }

  function updateBadge(count) {
    var n = parseInt(count, 10) || 0;
    var cartBtns = document.querySelectorAll('.ooo-nav__icon--cart');
    Array.prototype.slice.call(cartBtns).forEach(function (cartBtn) {
      var badge = cartBtn.querySelector('.ooo-cart-count');
      var prev = badge ? (parseInt(badge.textContent, 10) || 0) : 0;
      if (n > prev) {
        cartBtn.classList.remove('is-bumped');
        void cartBtn.offsetWidth; /* restart animation */
        cartBtn.classList.add('is-bumped');
        setTimeout(function () { cartBtn.classList.remove('is-bumped'); }, 600);
      }
      if (n > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'ooo-cart-count';
          badge.setAttribute('aria-hidden', 'true');
          cartBtn.appendChild(badge);
        }
        badge.textContent = n;
      } else if (badge) {
        badge.remove();
      }
      cartBtn.setAttribute('aria-label', 'Cart, ' + n + ' items');
    });
  }

  function refresh() {
    return fetch(location.pathname + '?section_id=' + SECTION_ID, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.text(); })
      .then(function (html) { applySection(html); })
      .catch(function () { /* noop */ });
  }

  function postCart(url, body) {
    setBusy(true);
    var isForm = body instanceof FormData;
    return fetch(url, {
      method: 'POST',
      headers: isForm ? { 'Accept': 'application/json' } : { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: isForm ? body : JSON.stringify(body)
    })
      .then(function (res) { return res.json().catch(function () { return null; }); })
      .then(function (data) {
        if (data && data.sections && data.sections[SECTION_ID]) applySection(data.sections[SECTION_ID]);
        else return refresh().then(function () { return data; });
        return data;
      })
      .finally(function () { setBusy(false); });
  }

  function changeKey(key, qty) {
    return postCart('/cart/change.js', { id: key, quantity: qty, sections: SECTION_ID, sections_url: location.pathname });
  }

  /* Change a line's subscription plan (or drop to one-time). Shopify /cart/change.js
     accepts selling_plan on a line; selling_plan:null removes the subscription.
     If the store ever rejects the in-place swap, fall back to remove + re-add. */
  function changePlan(sel) {
    var key = sel.dataset.key;
    var variant = String(sel.dataset.variant || '').trim();
    var qty = parseInt(sel.dataset.quantity, 10) || 1;
    var val = String(sel.value || '').trim();
    var planId = (val && val !== 'once') ? Number(val) : null;
    setBusy(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty, selling_plan: planId, sections: SECTION_ID, sections_url: location.pathname })
    })
      .then(function (r) { return r.json().catch(function () { return null; }); })
      .then(function (data) {
        /* verify the swap actually took; otherwise remove + re-add the variant */
        var line = data && data.items ? data.items.filter(function (it) { return String(it.variant_id) === variant; })[0] : null;
        var got = line && line.selling_plan_allocation ? line.selling_plan_allocation.selling_plan.id : null;
        var wanted = planId;
        if (variant && ((wanted && got !== wanted) || (!wanted && got))) {
          return fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: key, quantity: 0 }) })
            .then(function () {
              var addBody = { id: Number(variant), quantity: qty, sections: SECTION_ID, sections_url: location.pathname };
              if (wanted) addBody.selling_plan = wanted;
              return postCart('/cart/add.js', addBody);
            });
        }
        if (data && data.sections && data.sections[SECTION_ID]) applySection(data.sections[SECTION_ID]);
        else return refresh();
        return data;
      })
      .finally(function () { setBusy(false); });
  }

  function addForm(form) {
    // Graceful no-op on an EMPTY store: if there's no real variant id, don't fetch.
    var idField = form.querySelector('[name="id"]');
    var idVal = idField ? String(idField.value || '').trim() : '';
    if (!idVal) {
      // open the drawer to its current (likely empty) state — never break.
      open();
      return Promise.resolve({ status: 'noop' });
    }
    var fd = new FormData(form);
    fd.append('sections', SECTION_ID);
    fd.append('sections_url', location.pathname);
    return postCart('/cart/add.js', fd);
  }

  /* ── Delegated clicks (close, qty, remove) ── */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-ooo-cart-close]')) { e.preventDefault(); return close(); }

    var rem = e.target.closest('[data-ooo-line-remove]');
    if (rem) { e.preventDefault(); changeKey(rem.dataset.key, 0); return; }

    var minus = e.target.closest('[data-ooo-qty-minus]');
    if (minus) { e.preventDefault(); changeKey(minus.dataset.key, parseInt(minus.dataset.qty, 10)); return; }

    var plus = e.target.closest('[data-ooo-qty-plus]');
    if (plus) { e.preventDefault(); changeKey(plus.dataset.key, parseInt(plus.dataset.qty, 10)); return; }
  });

  /* ── ONE global add-to-cart intercept ── */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[action*="/cart/add"], form[action$="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    var btn = form.querySelector('[type="submit"]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    addForm(form)
      .then(function (data) { if (!(data && data.status && data.status !== 'noop')) open(); })
      .finally(function () { if (btn) btn.removeAttribute('aria-busy'); });
  });

  /* ── Per-line subscription / frequency dropdown ── */
  document.addEventListener('change', function (e) {
    var sel = e.target.closest('[data-ooo-line-plan]');
    if (sel) changePlan(sel);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.classList.contains('ooo-cart-open')) close();
  });

  /* ── External triggers (e.g. bundle "add all") ── */
  document.addEventListener('ooo:cart:refresh', function () { refresh().then(open); });

  window.OOOCart = { open: open, close: close, refresh: refresh };
})();
